import {
  canCarryRunLogs,
  nextRunLogCursor,
  RUN_LOGS_PER_PAGE,
  type RunLogScope,
  toRunLogLines,
} from './run-log-lines';
import {
  LOG_LEVEL_TO_API,
  type ParsedLogQuery,
} from '@/components/v1/cloud/logging/log-search/types';
import { compareTimestamps } from '@/components/v1/cloud/logging/log-timeline';
import { useCurrentTenantId } from '@/hooks/use-tenant';
import { type V1LogLine, V1LogLineOrderByDirection } from '@/lib/api';
import api from '@/lib/api/api';
import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

type RunLogPage = { rows: V1LogLine[]; nextCursor?: string };
type RunLogCursor = { until?: string; through?: string };
type RunLogPages = InfiniteData<RunLogPage, RunLogCursor>;

type LoadedRunLogs = {
  scope: RunLogScope;
  data: RunLogPages;
};

export function useRunLogs(
  taskExternalIds: string[],
  query: ParsedLogQuery,
  isPollingEnabled = true,
) {
  const { tenantId } = useCurrentTenantId();
  const taskIdsKey = JSON.stringify([...new Set(taskExternalIds)].sort());
  const filterKey = JSON.stringify([query.level, query.search, query.attempt]);
  const scope = useMemo<RunLogScope>(
    () => ({ tenantId, filterKey, taskIds: JSON.parse(taskIdsKey) }),
    [tenantId, filterKey, taskIdsKey],
  );
  const { taskIds } = scope;
  const loadedRef = useRef<LoadedRunLogs>();
  const filters = {
    limit: RUN_LOGS_PER_PAGE,
    taskExternalIds: taskIds,
    ...(query.level && { levels: [LOG_LEVEL_TO_API[query.level]] }),
    ...(query.search && { search: query.search }),
    ...(query.attempt !== undefined && { attempt: query.attempt }),
    order_by_direction: V1LogLineOrderByDirection.DESC,
  };
  const logsQuery = useInfiniteQuery<
    RunLogPage,
    Error,
    RunLogPages,
    QueryKey,
    RunLogCursor
  >({
    queryKey: [
      'workflow-run-logs',
      tenantId,
      taskIds,
      query.level,
      query.search,
      query.attempt,
    ],
    queryFn: async ({ pageParam, signal }) => {
      const rows: V1LogLine[] = [];
      let until = pageParam.until;
      for (;;) {
        const response = await api.v1TenantLogLineList(
          tenantId,
          { ...filters, until },
          { signal },
        );
        rows.push(...(response.data.rows ?? []));
        const nextCursor = nextRunLogCursor(response.data);
        if (
          !pageParam.through ||
          !nextCursor ||
          (compareTimestamps(nextCursor, pageParam.through) ?? 0) <= 0
        ) {
          return { rows, nextCursor };
        }
        if (until && (compareTimestamps(nextCursor, until) ?? 0) >= 0) {
          throw new Error('Log pagination did not advance to older history.');
        }
        until = nextCursor;
      }
    },
    initialPageParam: {},
    getNextPageParam: (page) =>
      page.nextCursor ? { until: page.nextCursor } : undefined,
    // New tasks can fill additional pages ahead of the history already read.
    // Refetch through its oldest timestamp; preserving only the page count
    // would discard older rows as the task scope grows.
    initialData: () => {
      const loaded = loadedRef.current;
      if (!loaded || !canCarryRunLogs(loaded.scope, scope)) {
        return undefined;
      }
      const rows = loaded.data.pages.flatMap((page) => page.rows);
      const lastPage = loaded.data.pages[loaded.data.pages.length - 1];
      return {
        pages: [{ rows, nextCursor: lastPage.nextCursor }],
        pageParams: [{ through: rows[rows.length - 1]?.createdAt }],
      };
    },
    initialDataUpdatedAt: 0,
    enabled: !!tenantId && taskIds.length > 0,
    refetchInterval: isPollingEnabled ? 2000 : false,
  });

  // Data and scope come from the same render, so the recorded pair always
  // describes rows fetched for (a subset of) the recorded task list.
  useEffect(() => {
    if (logsQuery.data) {
      loadedRef.current = { scope, data: logsQuery.data };
    }
  }, [logsQuery.data, scope]);

  const logs = useMemo(
    () => toRunLogLines(logsQuery.data?.pages ?? []),
    [logsQuery.data],
  );
  const fetchOlderLogs = useCallback(() => {
    if (logsQuery.hasNextPage && !logsQuery.isFetchingNextPage) {
      void logsQuery.fetchNextPage();
    }
  }, [logsQuery]);

  return { logs, logsQuery, fetchOlderLogs };
}
